import { message } from "@shared/antd/message";
import { Form } from "@shared/antd/form";
import { Input } from "@shared/antd/input";
import { Card } from "@shared/antd/card";
import React, { createElement, useEffect, useMemo, useState } from "react";
import { Button as AntButton } from "@shared/antd/button";
import { FormComponentProps } from "antd/lib/form";
import { goBackOrHomePage, makeCollectionContainer } from "../../../util";
import Section from "../../../hoc/Section";
import Page from "../../../hoc/PageContentHoc";
import Button, { ButtonType } from "../../../components/Button/Button";
import { useHistory } from "react-router-dom";
import { useApplicationForm } from "app/entities/form-controller/lib/use-application-form";
import { useApplicationStore } from "app/entities/form-controller/abstract";
import { ApplicationState } from "app/entities/form-controller/model/store";
import { FormWrapper } from "app/entities/form-controller/ui/form-wrapper";
import { FormattedMessage, useIntl } from "react-intl";
import { Msg } from "@cuba-platform/react";
import { mainStore, rootStore } from "../../../store";
import { JobDescriptionRequestKzm } from "cuba/entities/kzm_JobDescriptionRequestKzm";
import { PositionGroupDetails } from "cuba/entities/base/tsadv_PositionGroupDetails";
import { TextArea } from "app/shared/antd/text-area";
import { PositionHierarchyTree } from "app/entities/position-hierarchy";
import { usePath } from "../../../../path-context";
import Notification from "../../../util/Notification/Notification";
import { JobDescriptionSection } from "app/features/job-description-request/ui/form-fields";
import { usePositionGroup } from "app/features/job-description-request/model/usePositionGroup";
import { usePositionDetails } from "app/features/job-description-request/model/usePositionDetails";
import { jobDescriptionFields } from "app/features/job-description-request/model/fields";
import { runReport } from "../../../util/reportUtil";
import { MultiFileUpload } from "app/features/job-description-request/ui/job-description-upload";
import { NewReadonlyField } from "../../../components/ReadonlyField/new-read-only";

type JobDescriptionRequestFormProps = FormComponentProps & {
  onSubmit?: (fields: Record<string, string>) => void;
  onStatusChange?: (status: string) => void;
};

const CODE_TEMPLATE: Record<string, string> = {
  // Специалист, Другие служащие (технические исполнители)
  "50": "SPECIALIST_JOBDESCRIPTION",
  "40": "SPECIALIST_JOBDESCRIPTION",

  // Руководитель
  "30": "MANAGER_JOBDESCRIPTION",

  // Рабочие профессии
  "20": "EMPLOYEE_JOBDESCRIPTION",
  "10": "EMPLOYEE_JOBDESCRIPTION",
};

const JobDescriptionRequestFormComponent: React.FC<
  JobDescriptionRequestFormProps
> = ({ form }) => {
  const store =
    useApplicationStore<ApplicationState<JobDescriptionRequestKzm>>();
  const {
    updateAndCommit,
    dataInstance,
    initialValue,
    validate,
    resetData,
    setIsSaving,
    isSaving,
    defaultValues,
  } = store();
  const bpmStore = store((s) => s.bpmStore);
  const dicStatuses = store((s) => s.dicStatuses);
  const { entityName } = store((s) => s.initialValue);
  const { isNotDraft, status, isEnd } = store((s) => s.getStatusRequest());
  const history = useHistory();
  const { isLoading } = useApplicationForm(form);
  const intl = useIntl();
  const { buildPath, getPath } = usePath();

  const [selectedPositions, setSelectedPositions] = useState<string | null>(
    null,
  );

  const { data: PositionGroup } = usePositionGroup(selectedPositions);
  const { data: positionDetail } = usePositionDetails(selectedPositions);

  useEffect(() => {
    if (initialValue.id === "new" && PositionGroup?.jobDescription) {
      form.setFieldsValue({
        generalAdditionalRequirements:
          PositionGroup.jobDescription.generalAdditionalRequirements,
        positionDuties: PositionGroup.jobDescription.positionDuties,
        compulsoryQualificationRequirements:
          PositionGroup.jobDescription.compulsoryQualificationRequirements,
        basicInteractionsAtWork:
          PositionGroup.jobDescription.basicInteractionsAtWork,
        objectivePosition: PositionGroup.jobDescription.objectivePosition,
        responsibilities: PositionGroup.jobDescription.responsibilities,
      });
    }
  }, [PositionGroup]);

  useEffect(() => {
    if (dataInstance?.positionGroup?.id)
      setSelectedPositions(dataInstance.positionGroup.id);
  }, [dataInstance?.positionGroup?.id]);

  useEffect(() => {
    console.log(defaultValues);
    if (defaultValues?.positionGroup?.id) {
      changeSelectedPosition(defaultValues.positionGroup.id);
    }
    return () => {
      form.resetFields();
      resetData();
      setSelectedPositions(null);
    };
  }, []);

  const changeSelectedPosition = React.useCallback((positionId: string) => {
    setSelectedPositions(positionId);
    form.setFieldsValue({ positionGroup: positionId });
  }, []);

  const saveRequest = React.useCallback(
    async (e: React.MouseEvent) => {
      e.preventDefault();
      if (!(await validate())) return;
      setIsSaving(true);
      try {
        const result = await updateAndCommit();
        Notification.success({
          message: intl.formatMessage({ id: "management.editor.success" }),
        });
        if (initialValue.id === "new") {
          const path = `/${buildPath(getPath("jobDescriptionRequest"), { entityId: result.id })}`;
          history.push(path);
        }
      } catch (err) {
        console.error(err);
        Notification.error({
          message: intl.formatMessage({ id: "errorOnSaving" }),
        });
      } finally {
        setIsSaving(false);
      }
    },
    [validate, updateAndCommit, initialValue.id],
  );

  const activeTask = bpmStore((s) => s.activeTask);
  const activeUserTask = bpmStore((s) => s.activeUserTask);

  const { isCandBTask, isCanChangeFiles } = useMemo(() => {
    if (!activeUserTask) {
      return {
        isTranslator: false,
        isCandBTask: false,
        isCanChangeFiles: false,
      };
    }

    const taskKey = activeTask?.taskDefinitionKey || "";

    const isTranslator = taskKey === "translator_task";
    const isCandBTask = ["c_and_b_task", "c_and_b_task2"].includes(taskKey);
    const isCanChangeFiles = isTranslator || isCandBTask;

    return { isTranslator, isCandBTask, isCanChangeFiles };
  }, [activeUserTask, activeTask]);

  const code = positionDetail?.dicEmployeeCategory?.code;
  const isFirst = ["10", "20"].includes(code || "");

  const actions = React.useMemo(
    () => [
      <Button
        key="close"
        buttonType={ButtonType.FOLLOW}
        onClick={() => goBackOrHomePage(history)}
      >
        {intl.formatMessage({ id: "close" })}
      </Button>,
      <Button
        key="save"
        style={{ backgroundColor: "green" }}
        onClick={saveRequest}
      >
        <FormattedMessage id="management.editor.submit" />
      </Button>,
    ],
    [saveRequest],
  );

  // 🔹 Конфиг для readonly полей

  const DownloadReportButton: React.FC = () => {
    const intl = useIntl();
    const code = positionDetail?.dicEmployeeCategory?.code;

    const handleDownload = async () => {
      const hide = message.loading(
        intl.formatMessage({ id: "vacancyRequestEdit.loadingUsers" }),
        0,
      );

      try {
        await updateAndCommit();

        const locale = rootStore.userInfo.locale?.toUpperCase();
        const template = code ? CODE_TEMPLATE[code] : undefined;

        if (!dataInstance || !locale || !template) {
          hide();
          message.warning(intl.formatMessage({ id: "report.invalid.data" }));
          return;
        }

        await runReport(
          template,
          {
            template: locale,
            parameters: [{ name: "req", value: dataInstance.id }],
          },
          intl,
        );

        hide();
      } catch (error) {
        console.error("Ошибка при загрузке отчета", error);
        hide();
      }
    };

    return (
      <AntButton
        type="link"
        disabled={initialValue.id === "new"}
        onClick={handleDownload}
        style={{ marginTop: 10, marginBottom: 10 }}
      >
        {intl.formatMessage({ id: "downloadJDTemplate" })}
      </AntButton>
    );
  };

  const NewReadonlyFieldsConfig = [
    { property: "requestNumber", style: { marginBottom: 12 } },
    { property: "requestDate", style: { marginBottom: 12 } },
    {
      property: "status",
      style: { marginBottom: 12 },
      options: makeCollectionContainer(dicStatuses),
    },
  ];

  const fileRule = isCandBTask
    ? [
        {
          required: true,
          message: intl.formatMessage(
            { id: "form.validation.required" },
            {
              fieldName:
                mainStore?.messages?.[
                  JobDescriptionRequestKzm.NAME + ".file"
                ] || "",
            },
          ),
        },
      ]
    : [];

  return (
    <Page
      pageName={intl.formatMessage({
        id: "jobDescriptionRequestEdit.form.caption",
      })}
    >
      <Section size="large">
        <FormWrapper
          form={form}
          onSubmit={(v) => console.log(v)}
          beforeSubmit={(v) => ({ ...v, comment: v.comment?.trim() })}
          isLoading={isLoading || isSaving}
          actionsButtons={actions}
        >
          {() => (
            <>
              <NewReadonlyField
                entityName={entityName}
                propertyName="positionGroup"
                form={form}
                disabled
                formItemOpts={{ style: { display: "none" } }}
              />
              {NewReadonlyFieldsConfig.map((f) => (
                <NewReadonlyField
                  key={f.property}
                  entityName={entityName}
                  propertyName={f.property}
                  form={form}
                  disabled
                  formItemOpts={{ style: f.style }}
                  optionsContainer={f.options}
                />
              ))}
              {initialValue.id === "new" && !defaultValues.positionGroup && (
                <PositionHierarchyTree
                  rootPositionId={rootStore.userInfo.positionId}
                  changeSelectedPosition={changeSelectedPosition}
                />
              )}
              <Form.Item
                label={createElement(Msg, {
                  entityName,
                  propertyName: "positionGroup",
                })}
              >
                <Input disabled value={PositionGroup?._instanceName} />
              </Form.Item>
              <Form.Item
                label={intl.formatMessage({
                  id: "jobDescriptionRequest.structure",
                })}
              >
                <TextArea
                  disabled
                  value={positionDetail?.structuralOrganizationsTree}
                  rows={4}
                />
              </Form.Item>
              {["functionalManagerName", "administrativeManagerName"].map(
                (field) => (
                  <Form.Item
                    key={field}
                    label={createElement(Msg, {
                      entityName: PositionGroupDetails.NAME,
                      propertyName: field,
                    })}
                  >
                    <Input value={positionDetail?.[field] || ""} disabled />
                  </Form.Item>
                ),
              )}
              <JobDescriptionSection
                fields={
                  isFirst
                    ? jobDescriptionFields.first
                    : jobDescriptionFields.second
                }
                form={form}
                entityName={entityName}
                disabled={isNotDraft()}
                dataInstance={dataInstance}
              />
              <NewReadonlyField
                entityName={entityName}
                propertyName="description"
                disabled={isNotDraft()}
                form={form}
                formItemOpts={{
                  style: { marginBottom: 12 },
                  label: intl.formatMessage({
                    id: "jobDescriptionRequestEdit.description",
                  }),
                }}
                getFieldDecoratorOpts={{
                  rules: [
                    { required: true, message: "Обязательное поле" },
                    {
                      max: 2000,
                      message: "Максимум 2000 символов",
                    },
                  ],
                }}
                component={Input.TextArea}
                extraProps={{ rows: 4 }}
              />
              <Card size="small" title={<FormattedMessage id="attachments" />}>
                {["ruFiles", "kzFiles", "enFiles"].map((field) => (
                  <div className={"m-4"} key={field}>
                    <Form.Item
                      key={field}
                      label={<FormattedMessage id={field} />}
                    >
                      {form.getFieldDecorator(field)(
                        <MultiFileUpload
                          isVisible={!isEnd() && isCanChangeFiles}
                        />,
                      )}
                    </Form.Item>
                  </div>
                ))}
              </Card>

              <DownloadReportButton />

              <NewReadonlyField
                customLabel={<FormattedMessage id="menu.documents-sign" />}
                entityName={JobDescriptionRequestKzm.NAME}
                propertyName="file"
                form={form}
                disabled={isEnd() || bpmStore.getState().isUserInitiator}
                formItemOpts={{
                  style: {
                    marginBottom: "12px",
                    display: isCandBTask || isEnd() ? undefined : "none",
                  },
                }}
                getFieldDecoratorOpts={{
                  rules: fileRule,
                  initialValue:
                    form.getFieldValue("file") || dataInstance?.file,
                }}
              />
            </>
          )}
        </FormWrapper>
      </Section>
    </Page>
  );
};

export const JobDescriptionRequestForm =
  Form.create<JobDescriptionRequestFormProps>()(
    JobDescriptionRequestFormComponent,
  );
